import React from 'react';
import {
  Drawer, List, ListItem, ListItemIcon, ListItemText,
  ListItemButton, Box, Typography, Divider
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  School as TrainingIcon,
  Assignment as AssignmentIcon,
  Description as DocumentIcon,
  Checklist as ChecklistIcon,
  Article as LogIcon,
  People as PeopleIcon,
  Assessment as ReportIcon,
  Edit as ContentIcon,
  PlayArrow as TakeTrainingIcon,
  AssignmentTurnedIn as MyAssignmentIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface MenuItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  allowRoles?: ('admin' | 'manager' | 'employee')[];
  requireRole?: 'admin' | 'manager' | 'employee';
}

interface NavigationProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

const Navigation: React.FC<NavigationProps> = ({ mobileOpen, onMobileClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const menuItems: MenuItem[] = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    // Employee accessible items
    { text: 'My Assignments', icon: <MyAssignmentIcon />, path: '/my-assignments' },
    
    // Manager and Admin items
    { text: 'Assignments', icon: <AssignmentIcon />, path: '/assignments', allowRoles: ['admin', 'manager'] },
    { text: 'Log Entries', icon: <LogIcon />, path: '/logs/entries', allowRoles: ['admin', 'manager'] },
    
    // Admin only items
    { text: 'Users', icon: <PeopleIcon />, path: '/users', requireRole: 'admin' },
    { text: 'Trainings', icon: <TrainingIcon />, path: '/trainings', requireRole: 'admin' },
    { text: 'Checklists', icon: <ChecklistIcon />, path: '/checklists', requireRole: 'admin' },
    { text: 'Documents', icon: <DocumentIcon />, path: '/documents', requireRole: 'admin' },
    { text: 'Logs', icon: <LogIcon />, path: '/logs', requireRole: 'admin' },
    { text: 'Audit Dashboard', icon: <ReportIcon />, path: '/audit-dashboard', requireRole: 'admin' },
  ];

  const isMenuItemVisible = (item: MenuItem): boolean => {
    if (!user) return false;

    // If no role restrictions, show to all authenticated users
    if (!item.requireRole && !item.allowRoles) return true;

    // Check specific role requirement
    if (item.requireRole && user.role !== item.requireRole) return false;

    // Check allowed roles
    if (item.allowRoles && !item.allowRoles.includes(user.role)) return false;

    return true;
  };

  const visibleMenuItems = menuItems.filter(isMenuItemVisible);

  // Group menu items by category
  const employeeItems = visibleMenuItems.filter(item => 
    item.path === '/' || item.path === '/my-assignments'
  );
  
  const managementItems = visibleMenuItems.filter(item => 
    item.path === '/assignments' || item.path === '/logs/entries'
  );
  
  const adminItems = visibleMenuItems.filter(item => 
    ![...employeeItems, ...managementItems].includes(item)
  );

  const renderMenuSection = (title: string, items: MenuItem[]) => {
    if (items.length === 0) return null;

    return (
      <React.Fragment>
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="overline" color="text.secondary" fontSize="0.75rem">
            {title}
          </Typography>
        </Box>
        {items.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => {
                navigate(item.path);
                onMobileClose();
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
        {title !== 'Administration' && <Divider sx={{ my: 1 }} />}
      </React.Fragment>
    );
  };

  const renderDrawerContent = () => (
    <Box sx={{ overflow: 'auto' }}>
      <List>
        {renderMenuSection('Dashboard', employeeItems)}
        {renderMenuSection('Management', managementItems)}
        {renderMenuSection('Administration', adminItems)}
      </List>
    </Box>
  );

  const paperSx = {
    width: 240,
    boxSizing: 'border-box' as const,
  };

  return (
    <>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': paperSx,
        }}
      >
        {renderDrawerContent()}
      </Drawer>
      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: 'none', md: 'block' },
          width: 240,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            ...paperSx,
            position: 'fixed',
            top: 64,
            left: 0,
            height: 'calc(100vh - 64px)',
          },
        }}
      >
        {renderDrawerContent()}
      </Drawer>
    </>
  );
};

export default Navigation; 